import {
	useGetCreditNoteLazyQuery,
	useGetContactLazyQuery,
	useGetProjectLazyQuery,
	CreditNoteKind,
	DocumentRole,
} from "@repo/server-api";

import { ResultFromData, ResultFromException, useNotificationGraphql } from "@repo/shared/common";

import type { GraphqlQueryResult } from "@repo/shared/common";
import type { CreditNote } from "../types/CommercialDocumentTypes";
import type { CreditNote as ServerCreditNote, Contact, Project } from "@repo/server-api";
import { IdWithName } from "@repo/shared/common";
import { normalizeContact, normalizeProject } from "@repo/legacy/components/helper/Index";

export function useCreditNoteLoader({
	onFetched,
}: {
	onFetched: (creditNote: CreditNote) => void;
}) {
	const [fetchCreditNote] = useGetCreditNoteLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchContact] = useGetContactLazyQuery({
		fetchPolicy: "no-cache",
	});

	const [fetchProject] = useGetProjectLazyQuery({
		fetchPolicy: "no-cache",
	});

	const contactGetter = async (id: string): Promise<IdWithName | null> => {
		let contact: IdWithName | null = null;

		contact = await fetchContact({ variables: { id } })
			.then((result) => {
				if (result.data?.node && (result.data.node as Contact).__typename === "Contact") {
					return normalizeContact(result.data.node as Contact);
				}
				return null;
			})
			.catch(() => {
				return null;
			});

		return contact;
	};

	const projectGetter = async (id: string): Promise<IdWithName | null> => {
		let project: IdWithName | null = null;

		project = await fetchProject({ variables: { id } })
			.then((result) => {
				if (
					result.data?.node &&
					(result.data.node as unknown as Project).__typename === "Project"
				) {
					return normalizeProject(result.data.node as unknown as Project);
				}
				return null;
			})
			.catch(() => {
				return null;
			});

		return project;
	};

	const mapCreditNote = async (node: ServerCreditNote): Promise<CreditNote> => {
		const client = node.clientId ? await contactGetter(node.clientId) : null;
		const contractor = node.contractorId ? await contactGetter(node.contractorId) : null;
		const contactPerson = node.clientContactPersonId
			? await contactGetter(node.clientContactPersonId)
			: null;
		const project = node.projectId ? await projectGetter(node.projectId) : null;

		return {
			id: node.id,
			number: node.number ?? "",
			project: project,
			leftTotal: node.grossTotal - node.paidTotal,
			grossTotal: node.grossTotal,
			issueDate: node.issueDate ?? "",
			dueDate: node.dueDate ?? "",
			client: client,
			type: node.documentType,
			status: node.status,
			contactPerson: contactPerson,
			contractor: contractor,
			paymentDeadlineDays: node.paymentDeadlineInDays,
			headerNote: node.introductoryText ?? null,
			footerNote: node.concludingText ?? null,
			items: node.items?.map((item) => ({
				id: item.id,
				name: item.name,
				description: item.description,
				discount: item.discountPercent ?? 0,
				quantity: item.quantity,
				unit: item.unit,
				unitPrice: item.unitPriceNet,
				totalPrice: item.lineNetTotal,
				tax: item.tax,
				taxCode: item.taxCode,
			})),
			notes: node.notes ?? null,
			netTotal: node.netTotal ?? null,
			// CreditNote specific
			kind: node.kind ?? CreditNoteKind.CustomerCreditMemo,
			documentRole: node.documentRole ?? DocumentRole.Sales,
			settlesInvoiceId: node.settlesInvoiceId ?? null,
			isProforma: node.isProforma,
			isInvalid: node.isInvalid,
			sentDate: node.sentDate ?? null,
		};
	};

	const fetchServerData = async (id: string): Promise<GraphqlQueryResult<CreditNote>> => {
		const result = await fetchCreditNote({ variables: { id } });

		if (result.error) return ResultFromException<CreditNote>(result.errors);

		const node = result.data?.node as unknown as ServerCreditNote;

		if (node?.__typename !== "CreditNote")
			return ResultFromException<CreditNote>([
				{
					message: "CreditNote not found",
				},
			]);

		return ResultFromData<CreditNote>(await mapCreditNote(node));
	};

	const { save: fetchData, loading } = useNotificationGraphql<string, CreditNote>({
		onComplete: (creditNote: CreditNote) => {
			onFetched(creditNote);
		},

		graphqlCall: fetchServerData,
	});

	return { fetchData, loading };
}
